<template>
  <connection-failure v-if="isUnknownError" :source="credsSource" :close-action="redirect"></connection-failure>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import DDLogs from '@/DDLogs';
import ConnectionFailure from '@/pages/responsive-pages/non-authenticated/open-banking/ConnectionFailure';
import { getSourceConfigurationByAggregatorID } from '@/constants/sourcesConfiguration';
import Segment from '@/Segment';
import LoadingPopup from '@/base-components/onboarding/LoadingPopup';
import AggregatorApi from '../../../../api/AggregatorApi';
import router from '../../../../router';
import consts from '../../../../constants/onboarding-state';

export default {
  name: 'OpenBankingLandingPage',
  components: {
    ConnectionFailure,
  },
  data() {
    return {
      isUnknownError: false,
      partiallyAuthorized: false,
      source: null,
    };
  },
  beforeDestroy() {
    this.closeModal();
  },
  async created() {
    const { state, code } = this.$route.query;
    DDLogs.log('OpenBankingLandingPage created', { state, code, source: this.$route.params.source });
    Segment.trackUserGot('OBKLandingPageEntered', { source: this.$route.params.source });
    this.openLoadingPopup();
    if (!code || !state) {
      this.displayError();
    } else {
      this.source = this.$route.params.source;
      try {
        await this.handleAccessCode(code, state);
      } catch (error) {
        if (error.response.status === 409) {
          Segment.trackUserGot('OBKLandingConsentNotValid', { source: this.$route.params.source });
          DDLogs.log('OBKLandingConsentNotValid tried to apply access code again - ignoring the error',
            { state, code, source: this.$route.params.source });
          await this.redirect();
        }
        DDLogs.error('OBKLandingPageError error applying access code',
          { state, code, source: this.$route.params.source, error: error.response.data });
        Segment.trackUserGot('OBKLandingPageError', { error });
        this.displayError();
      }
    }
  },
  computed: {
    ...mapState('genericError', ['ERROR_TYPES']),
    ...mapState('onboardingState', ['pageName']),
    isOnboarding() {
      return this.pageName && this.pageName !== consts.ONBOARDING_PAGE.CASHFLOW;
    },
    credsSource() {
      return getSourceConfigurationByAggregatorID(this.source);
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    ...mapMutations('onboardingState', ['setPageParams']),
    ...mapMutations('genericError', ['setErrorType']),
    ...mapActions('onboardingState', ['fetchOnboardingState',
      'bankDetailsOpenBankingPartiallyAuthorized', 'bankDetailsSubmitOpenBanking']),
    displayError() {
      this.isUnknownError = true;
      this.closeModal();
    },
    openLoadingPopup() {
      this.openModal({
        component: LoadingPopup,
        popupAlignment: 'center',
        props: {
          title: 'עוד רגע נמשיך...',
          subtitle: 'המערכת מתחברת לבנק',
        },
      });
    },
    async handleAccessCode(accessCode, consentId) {
      const { consentStatus, metaData } = await AggregatorApi.applyAccessCode(consentId, this.source, accessCode);
      const { isOnboarding } = this;
      if (!consentStatus) {
        this.handleInvalidResponse(consentStatus);
        return;
      }
      Segment.trackUserGot('OBKLandingPageSuccess', { isOnboarding, consentStatus });
      DDLogs.log('OBKLandingPageSuccess apply access code success', { consentStatus });
      if (isOnboarding) {
        await this.onboardingRedirect(consentStatus, metaData.credentialsId, this.credsSource.sourceName);
      } else {
        await this.cashflowRedirect();
      }
    },
    async redirect() {
      if (this.isOnboarding) {
        const sourceName = this.credsSource.displayName;
        await this.setPageParams({ sourceName });
        this.redirectToNewOnboarding();
      } else {
        this.redirectToCredentialsPage();
      }
    },
    handleInvalidResponse(consentStatus) {
      DDLogs.error('Received empty consentStatus', { isOnboarding: this.isOnboarding, consentStatus });
      Segment.trackUserGot('OBKLandingPageErrorEmptyConsentStatus', { isOnboarding: this.isOnboarding, errorType: 'invalid-response' });
      this.displayError();
    },
    async onboardingRedirect(consentStatus, credentialsId, sourceName) {
      DDLogs.error('onboardingRedirects', { consentStatus, credentialsId, sourceName });
      if (consentStatus === 'partiallyAuthorised') {
        Segment.trackUserGot('OBKLandingPagePartiallyAuthorised', { sourceName });
        await this.bankDetailsOpenBankingPartiallyAuthorized();
      } else {
        await this.bankDetailsSubmitOpenBanking({ credentialsId, sourceName });
      }
      await this.fetchOnboardingState();
      this.redirectToNewOnboarding();
    },
    async cashflowRedirect() {
      await this.redirectToCredentialsPage();
    },
    redirectToNewOnboarding() {
      router.push({ path: '/ob' });
    },
    redirectToCredentialsPage() {
      router.push({ path: '/credentials' });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../scss/mixins';
</style>
